/**
 * Receipts + refunds API (Phase 12.5)
 *
 * Backend endpoints under /api/organisation:
 *
 *   GET    /visits/{visit_id}/receipts                 — list every Payment on a visit
 *     ?include_voided=true                                 (audit; default false)
 *   POST   /payments/{payment_id}/refund                — record a refund against a settled payment
 *     body: { amount_rupees, reason, method? }
 *   GET    /payments/{payment_id}/refund-slip           — PDF blob for a REFUND payment
 *
 *   Reuses two existing helpers in patientPlanApi.js:
 *     openProcedureReceiptPdf(paymentId)               — single-payment receipt
 *     openCombinedReceiptPdf(paymentIds[])             — bundled multi-payment receipt
 *
 * Every function returns `{ success, data?, error? }`. Error messages
 * prefer the server's `detail` verbatim.
 */
import apiClient from './apiClient';


function buildApiError(error, fallback) {
  const data = error?.response?.data;
  const status = error?.response?.status;
  const serverDetail = data?.detail;
  if (serverDetail && typeof serverDetail === 'object') {
    if (serverDetail.message) return serverDetail.message;
    return JSON.stringify(serverDetail);
  }
  if (typeof serverDetail === 'string') return serverDetail;
  if (data?.message) return data.message;
  switch (status) {
    case 400: return 'Invalid request — please check the form.';
    case 401: return 'Session expired. Please login again.';
    case 403: return 'You do not have permission for this action.';
    case 404: return 'Not found.';
    case 409: return 'That action conflicts with the current state.';
    case 500: return 'Server error. Please try again in a moment.';
    default:  return fallback;
  }
}

function handleError(error, fallback) {
  // eslint-disable-next-line no-console
  console.error('[ReceiptsAPI]', error);
  return { success: false, error: buildApiError(error, fallback) };
}


/**
 * GET /visits/{visit_id}/receipts
 *
 * Returns every canonical Payment attached to the visit, in renderer
 * shape — used by the receipts modal to populate the picker and
 * compute refundable remainders.
 *
 * Response: { receipts: [
 *   { payment_id, source_type, parent_payment_id, receipt_number,
 *     amount_rupees, net_amount_rupees, refunded_so_far_rupees,
 *     refundable_remaining_rupees, collected_at, description,
 *     method, voided }
 * ]}
 *
 * VOIDED rows are excluded by default; pass includeVoided=true for audit.
 */
export const listReceiptsForVisit = async (visitId, { includeVoided = false } = {}) => {
  try {
    const params = includeVoided ? { include_voided: true } : {};
    const r = await apiClient.get(
      `/api/organisation/visits/${visitId}/receipts`,
      { params },
    );
    return { success: true, data: r.data?.receipts || [] };
  } catch (err) {
    return handleError(err, 'Could not load receipts for visit.');
  }
};


// Shared row predicate — a Payment row counts toward "collected" when it is
// neither voided nor a REFUND row. Both `sumCollectedRupees` and
// `tokenLineItemsFromReceipts` (Issue-1) filter through this ONE function so
// the two can never drift (RC-1): Σ(line items) === sumCollectedRupees(receipts)
// by construction, since a zero-amount payable row contributes 0 either way.
const isPayableRow = (r) => !r.voided && r.source_type !== 'REFUND';

/**
 * sumCollectedRupees(receipts) — TOKEN-UPDATE T1 FE5 / GATE 3 RULING 2
 *
 * Single source of truth for "total collected on this visit" — sums every
 * non-REFUND, non-voided Payment row's `amount_rupees`. Refunds add negative
 * REFUND child rows and leave the parent's `amount_rupees` unchanged, so
 * this total is stable across reprints (registration-time total == reprint
 * total, RC5). Consumed by the token's Amount Paid row (registration +
 * reprint) and by VisitReceiptsModal's "Collected" summary tile — one
 * definition, both surfaces.
 */
export const sumCollectedRupees = (receipts = []) => {
  const list = Array.isArray(receipts) ? receipts : [];
  return list.reduce((sum, r) => {
    if (!isPayableRow(r)) return sum;
    return sum + Number(r.amount_rupees || 0);
  }, 0);
};


/**
 * tokenLineItemsFromReceipts(receipts) — Issue-1 (2026-07-31)
 *
 * Single source of truth for the thermal token's per-scan line items — maps
 * every payable Payment row (same `isPayableRow` predicate as
 * `sumCollectedRupees`, so the two can never drift, RC-1) with a positive
 * amount to `{ name, amount }`. Zero-amount rows (e.g. skip_payment) are
 * dropped from the printed list but already contribute 0 to
 * `sumCollectedRupees`, so the invariant
 * `sum(tokenLineItemsFromReceipts(r).map(li => li.amount)) === sumCollectedRupees(r)`
 * holds by construction (RC-1, asserted by a unit test).
 *
 * W2/C1 — a row carrying a `quantity` > 1 (procedure billed qty × unit,
 * server-computed) also gets `{ quantity }` on the mapped line so
 * `printToken` can render "Name × N". A row with no `quantity` (old backend,
 * or qty === 1) omits the field — byte-identical to Issue-1 output.
 *
 * F9 — each mapped line also carries the row's `source_type` (additive;
 * existing consumers that only read `{ name, amount, quantity }` are
 * unaffected) so `shouldItemizeToken` can tell a LAB/PROCEDURE row apart
 * from a plain CONSULTATION/REGISTRATION row without a second pass over
 * the raw receipts.
 *
 * Consumed by both visit-token callers (RegisterVisitForm registration,
 * VisitReceiptsModal reprint) — receipts are what was actually collected,
 * unlike form-side "intent" lines which can include failed/skipped payments.
 * Server row order (collected_at-ordered) is preserved.
 */
export const tokenLineItemsFromReceipts = (receipts = []) => {
  const list = Array.isArray(receipts) ? receipts : [];
  return list
    .filter((r) => isPayableRow(r) && Number(r.amount_rupees) > 0)
    .map((r) => {
      const qty = Number(r.quantity);
      const item = {
        name: r.description || '—',
        amount: Number(r.amount_rupees),
        source_type: r.source_type,
      };
      if (Number.isFinite(qty) && qty > 1) item.quantity = qty;
      return item;
    });
};


/**
 * shouldItemizeToken(receipts) — Issue-1 (2026-07-31), amended F9 (2026-08-02)
 *
 * Gate deciding whether the thermal token prints the itemized line-item
 * section or keeps the current single "Amount Paid" row (D1 layout). Owner
 * ruling (F9, 2026-08-02, amends Issue-1/RC-3): itemize when there are
 * >= 2 payable line items, OR any line derives from a LAB or PROCEDURE
 * receipts row — a lab/OTC visit with a single named test or procedure
 * must print its name, not vanish into a bare "Amount Paid" total. A
 * consultation/registration-only token (1 row, no LAB/PROCEDURE row) still
 * stays a single Amount Paid row, byte-identical to today (Issue-1
 * guarantee narrows to that case).
 *
 * The SAME gate backs both visit-token callers (RegisterVisitForm
 * registration, VisitReceiptsModal reprint) — neither re-derives the
 * itemize decision independently.
 */
export const shouldItemizeToken = (receipts = []) =>
  shouldItemizeTokenLines(tokenLineItemsFromReceipts(receipts));

/**
 * shouldItemizeTokenLines(lineItems) — F13 (2026-08-02)
 *
 * The itemize rule expressed over ALREADY-BUILT token line items (same shape
 * `tokenLineItemsFromReceipts` emits: `{ name, amount, source_type, quantity? }`).
 * `shouldItemizeToken(receipts)` delegates here so the F9 gate has exactly ONE
 * definition, whether the lines came from the receipts read-back OR from the
 * race-free collect-response derivation (`tokenMoneyFromCollect`). No behaviour
 * change for the receipts path — byte-identical result to the previous inline.
 */
export const shouldItemizeTokenLines = (lineItems = []) => {
  const lines = Array.isArray(lineItems) ? lineItems : [];
  return lines.length >= 2 || lines.some(
    (l) => l.source_type === 'LAB' || l.source_type === 'PROCEDURE',
  );
};

/**
 * tokenMoneyFromCollect(args) — F13 (2026-08-02): RACE-FREE token money.
 *
 * Root cause of the registration token missing Amount Paid / line items: the
 * SuccessPopup (and its "Print Token" button) render the instant
 * `registerVisit` resolves — BEFORE the post-collect `listReceiptsForVisit`
 * read-back has set the amount/line-item state. A fast click (or a slow/failed
 * read-back) printed a token whose money was still `null`, and the old
 * failure fallback (`visit.consultation_fee`) is 0 for an OTC/procedure visit
 * → the printed token showed nothing while the money WAS collected.
 *
 * Fix: derive the token's money from data the client ALREADY HOLDS the moment
 * the collect calls resolve — never from a read-back that can race:
 *   - `bundled` = the collectBundledPayment RESPONSE ({ total, components,
 *     errors }). `total` is the server-authoritative sum of consultation +
 *     registration + every collected sitting (== sumCollectedRupees by
 *     construction, RC5), so the Amount Paid is correct even on a partial
 *     failure.
 *   - `labCollected` = rupees the lab bundle just collected (form-known).
 *   - `billingOnly` = consult/registration written via updateVisitBilling when
 *     there were no procedures to bundle.
 *   - the register RESPONSE's own `visit.consultation_fee`/`registration_fee`
 *     are the last-resort held fallback (a fee-only visit with no collect leg).
 *
 * Line items are built from the form-held lines (which carry name + quantity),
 * MINUS any leg the server reported in `errors[]`, and are only emitted when
 * their sum RECONCILES with the authoritative `amountPaid` (else a single
 * Amount Paid row — the RC-1 invariant Σ(items) === amountPaid then holds by
 * construction; a partial-failure mismatch degrades gracefully and the
 * receipts reconcile upgrades it). The receipts read-back stays, but ONLY as a
 * reconcile/upgrade once it resolves — it never gates the print.
 */
export const tokenMoneyFromCollect = ({
  bundled = null,
  labCollected = 0,
  billingOnly = 0,
  visit = null,
  consultationFee = 0,
  registrationFee = 0,
  procedureLines = [],
  investigationLines = [],
} = {}) => {
  const lab = Number(labCollected) || 0;
  let amountPaid;
  if (bundled && bundled.total != null) {
    // bundled.total already folds consultation + registration + sittings.
    amountPaid = Number(bundled.total) + lab;
  } else if (Number(billingOnly) > 0) {
    amountPaid = Number(billingOnly) + lab;
  } else {
    // Fee-only visit with no collect leg: the register response's own fees.
    const vc = Number(visit?.consultation_fee || 0);
    const vr = Number(visit?.registration_fee || 0);
    amountPaid = vc + vr + lab;
  }

  // Legs the server rejected — matched by (plan_id, sitting_id) as emitted in
  // the bundled response `errors[]` (kind/action/plan_id/sitting_id/error).
  const failedKeys = new Set(
    (bundled?.errors || []).map((e) => `${e.plan_id || ''}:${e.sitting_id || ''}`),
  );
  const items = [];
  if (Number(consultationFee) > 0) {
    items.push({ name: 'Consultation', amount: Number(consultationFee), source_type: 'CONSULTATION' });
  }
  if (Number(registrationFee) > 0) {
    items.push({ name: 'Registration', amount: Number(registrationFee), source_type: 'REGISTRATION' });
  }
  (procedureLines || []).forEach((p) => {
    const amt = Number(p?.amount || 0);
    if (amt <= 0) return; // move_only / waived — billed 0, not on this token
    if (failedKeys.has(`${p.plan_id || ''}:${p.sitting_id || ''}`)) return;
    const item = { name: p.name || 'Procedure', amount: amt, source_type: 'PROCEDURE' };
    const qty = Number(p.quantity);
    if (Number.isFinite(qty) && qty > 1) item.quantity = qty;
    items.push(item);
  });
  (investigationLines || []).forEach((inv) => {
    if (!inv || inv.skip_payment) return;
    const qty = Number(inv.quantity) || 1;
    const amt = Number(inv.cost || 0) * qty;
    if (amt <= 0) return;
    const item = { name: inv.name || 'Lab Test', amount: amt, source_type: 'LAB' };
    if (qty > 1) item.quantity = qty;
    items.push(item);
  });

  const itemsSum = items.reduce((s, i) => s + Number(i.amount || 0), 0);
  const reconciles = Math.abs(itemsSum - amountPaid) < 0.005;
  const lineItems = reconciles && shouldItemizeTokenLines(items) ? items : null;
  return { amountPaid, lineItems };
};


/**
 * tokenReceiptNumberFromCollect(args) — F16 / W7 (2026-08-02): the token's
 * minted Receipt No. from data the client ALREADY HOLDS.
 *
 * Post-W7 the register response's `visit.receipt_number` is NULL — a receipt
 * number is minted at PAYMENT time, not at registration. So the SuccessPopup
 * can no longer read the number off the register response. This selector folds
 * the number out of the collect RESPONSE the client just received (same source
 * `tokenMoneyFromCollect` uses for the amount), so a PAID registration's token
 * prints its Receipt No. immediately, race-free — while an UNPAID registration
 * (no money leg) yields `null` and the token omits the row (printToken already
 * suppresses it when null).
 *
 *   - `bundled` = the collectBundledPayment RESPONSE. Its `batch_receipt_number`
 *     is the minted number for the whole bundle (consultation + registration +
 *     sittings); `visit.receipt_number` is the same number denormed onto the
 *     visit row (W7 seam). Either is authoritative.
 *   - `labOrders` = the updated orders the lab bundle collect returned; each
 *     carries the shared `receipt_number` denormed at collect. The lab bundle
 *     has no top-level batch field, so the first collected order's number is
 *     the visit's registration receipt for a lab-only visit.
 *
 * Precedence: a procedure/fee bundle mints the visit's registration receipt, so
 * it wins when present; the lab number is the fallback for a lab-only visit.
 */
// Internal — resolves the bare int AND its printed sibling from the SAME
// winning source (batch > visit > first collected lab order), so the two
// can never point at different transactions. `tokenReceiptNumberFromCollect`
// (unchanged return shape, back-compat) and `tokenReceiptNumberPrintedFromCollect`
// (GST-DOCNO/A2-d, §8 C-7 `receiptsApi.receiptNumberFrom()`) are both thin
// wrappers over this one resolution — "reads the printed field first and
// falls back to the int" has exactly one implementation.
const resolveTokenReceiptNumber = ({ bundled = null, labOrders = null } = {}) => {
  if (bundled) {
    if (bundled.batch_receipt_number != null) {
      return { number: bundled.batch_receipt_number, printed: bundled.receipt_number_printed ?? null };
    }
    if (bundled.visit?.receipt_number != null) {
      return { number: bundled.visit.receipt_number, printed: bundled.visit.receipt_number_printed ?? null };
    }
  }
  const orders = Array.isArray(labOrders) ? labOrders : [];
  for (const o of orders) {
    if (o && o.receipt_number != null) {
      return { number: o.receipt_number, printed: o.receipt_number_printed ?? null };
    }
  }
  return { number: null, printed: null };
};

export const tokenReceiptNumberFromCollect = (args) => resolveTokenReceiptNumber(args).number;

/**
 * tokenReceiptNumberPrintedFromCollect(args) — GST-DOCNO/A2-d, §8 C-7.
 *
 * The printed sibling of `tokenReceiptNumberFromCollect` — same precedence
 * (batch > visit > first lab order), same source per call, so the printed
 * string and the bare int the SuccessPopup token renders always describe
 * the same transaction. `null` when the winning source carries no printed
 * string (pre-change payment / older backend) — `printToken` then falls
 * back to the bare int, byte-identical to today.
 */
export const tokenReceiptNumberPrintedFromCollect = (args) => resolveTokenReceiptNumber(args).printed;


/**
 * refundLinesFromReceipts(receipts) — Issue-2 (2026-08-01), W7 (2026-08-02)

 *
 * Single source of truth for the token's dashed "Refunds" section — maps
 * every non-voided REFUND Payment row to `{ name, amount, receipt_number }`,
 * the SAME shape as `tokenLineItemsFromReceipts` / the token's `procedures`
 * param so `printToken` reuses the existing `.proc-row` rendering. `amount`
 * is the POSITIVE magnitude of the refund (`-net_amount_rupees` — REFUND
 * rows carry a negative net). `receipt_number` is the REFUND row's OWN
 * receipt (server twin: `organisation_visits.py::_refund_notes`). Server row
 * order (collected_at-ordered) is preserved.
 *
 * W7 — a row carrying `receipt_series: "CN"` (new credit-note refund) also
 * gets `{ receipt_series }` on the mapped line so `printToken` can render
 * "· CN N" instead of "· Rx N". A row with no `receipt_series` (historical
 * money-series refund, or old backend) omits the field — byte-identical to
 * pre-W7 output.
 */
export const refundLinesFromReceipts = (receipts = []) => {
  const list = Array.isArray(receipts) ? receipts : [];
  return list
    .filter((r) => !r.voided && r.source_type === 'REFUND')
    .map((r) => {
      const line = {
        name: r.description || 'Refund',
        amount: -Number(r.net_amount_rupees || 0),
        receipt_number: r.receipt_number ?? null,
      };
      if (r.receipt_series != null) line.receipt_series = r.receipt_series;
      return line;
    });
};


/**
 * netPaid(receipts) — Issue-2 (2026-08-01)
 *
 * = sumCollectedRupees(receipts) - Σ refundLinesFromReceipts(receipts).amount.
 * Composes the two selectors above, so it can never drift from either the
 * Collected total or the Refunds section — single source for the token's
 * "Net Paid" total AND VisitReceiptsModal's "Net for visit" tile.
 */
export const netPaid = (receipts = []) => {
  const collected = sumCollectedRupees(receipts);
  const refundedTotal = refundLinesFromReceipts(receipts)
    .reduce((sum, l) => sum + l.amount, 0);
  return collected - refundedTotal;
};


/**
 * POST /payments/{payment_id}/refund
 *
 * Records a refund against `paymentId`. The parent stays SETTLED unless
 * this refund completes the gross amount — then it flips to REFUNDED.
 *
 * Body: { amount_rupees, reason, method? }
 *   - amount_rupees:   string or number, ≤ parent's refundable remaining
 *   - reason:          free-text or one of the recommended slugs
 *   - method:          optional override; defaults to parent's method
 *
 * Returns the new REFUND payment summary on success.
 */
export const refundPayment = async (paymentId, { amount_rupees, reason, method, idempotency_key } = {}) => {
  try {
    const body = { amount_rupees, reason };
    if (method) body.method = method;
    // C-4: OPD refunds start sending an idempotency key (header optional /
    // back-compat on the server) so a double-confirm replays to ONE refund.
    const config = idempotency_key ? { headers: { 'X-Idempotency-Key': idempotency_key } } : undefined;
    const r = await apiClient.post(
      `/api/organisation/payments/${paymentId}/refund`,
      body,
      config,
    );
    return { success: true, data: r.data };
  } catch (err) {
    return handleError(err, 'Could not record refund.');
  }
};


/**
 * GET /visits/daily-summary?date=YYYY-MM-DD&doctor_id=<opt>
 *
 * Returns the day's collection totals (Cash / UPI / Card / Refunds /
 * Net) — used by the Manage Visit page's Daily Collection Summary
 * widget. Same data the Busy export reads, so on-screen tiles can
 * never drift from the spreadsheet.
 *
 * Response: {
 *   date, doctor_id,
 *   by_event: { CONSULTATION, REFERRAL, PROCEDURE, REFUND },
 *   by_mode:  { CASH, UPI, CARD, NETBANKING, WALLET, ... },
 *   gross_collected_rupees, total_refunded_rupees, net_collected_rupees,
 *   line_count,
 * }
 */
export const getDailyCollectionSummary = async (date, doctorId, provider) => {
  try {
    const params = { date };
    if (doctorId) params.doctor_id = doctorId;
    // Provider scope (Manage Visits OPD / NURSE / LAB filter) — omit for org-wide.
    if (provider) params.provider = provider;
    const r = await apiClient.get(
      '/api/organisation/visits/daily-summary', { params },
    );
    return { success: true, data: r.data };
  } catch (err) {
    return handleError(err, 'Could not load daily summary.');
  }
};


/**
 * C-6 POST /receipts/eligibility:batch (bk-017)
 *
 * ONE call takes every visible item and returns receipt/refund-eligibility for
 * all of them — replaces the per-row `listReceiptsForVisit` burst on a board.
 * `items` = [{ visit_id, patient_id } | { admission_id }], MAX 100 (server 400
 * BATCH_TOO_LARGE past that; we also guard client-side so a huge page never
 * fires a doomed request).
 *
 * Response: { results: { "<visit_id|admission_id>": {
 *   receipt_count, payments: [{ payment_id, source_type, status, net_paise,
 *     refundable_remaining_paise, refund_eligible, block_code }] } } }
 */
export const BATCH_MAX_ITEMS = 100;

export const fetchReceiptEligibilityBatch = async (items = []) => {
  try {
    const list = Array.isArray(items) ? items : [];
    if (list.length === 0) return { success: true, data: { results: {} } };
    if (list.length > BATCH_MAX_ITEMS) {
      return { success: false, error: `Too many items — split into pages of ${BATCH_MAX_ITEMS} or fewer.`, code: 'BATCH_TOO_LARGE' };
    }
    const r = await apiClient.post('/api/organisation/receipts/eligibility:batch', { items: list });
    return { success: true, data: { results: r.data?.results || {} } };
  } catch (err) {
    return handleError(err, 'Could not load receipt eligibility.');
  }
};


/**
 * GET /payments/{payment_id}/refund-slip
 *
 * Opens the refund slip PDF in the in-app FileViewerStore.
 * Returns 404 if the payment is not a REFUND row.
 */
export const openRefundSlipPdf = async (paymentId) => {
  try {
    const response = await apiClient.get(
      `/api/organisation/payments/${paymentId}/refund-slip`,
      { responseType: 'blob' },
    );
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const blobUrl = URL.createObjectURL(blob);
    const useFileViewerStore = (await import('../../stores/useFileViewerStore')).default;
    useFileViewerStore.getState().openViewer({
      blobUrl,
      fileType: 'pdf',
      title: 'Refund slip',
    });
    return { success: true, blobUrl };
  } catch (err) {
    return handleError(err, 'Could not generate refund slip.');
  }
};
